import React, { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { PaginationWrapper } from '@/components/organisms'
import { useOrgMemberInquiries } from '@/hooks/useOrganization'
import { cn } from '@/utils'
import type { OrgMembership } from '@/types/organization'
import {
  ORG_CARD,
  ORG_CARD_PADDING_DESKTOP,
  ORG_CARD_PADDING_MOBILE,
  ORG_HEADING,
  ORG_SUBHEADING,
  OrgErrorBanner,
  RoleBadge,
  memberName,
} from '../OrgPrimitives'
import { mergeMemberInquiries, mergedPageCount } from './MemberInquiriesBase'
import MemberInquiriesDesktop from './MemberInquiriesDesktop'
import MemberInquiriesMobile from './MemberInquiriesMobile'

interface MemberInquiriesPanelProps {
  orgId: string
  member: OrgMembership
  isMobile: boolean
  onClose: () => void
}

/** Backend default; it clamps `limit` to [1, 50]. */
const PAGE_LIMIT = 10

/**
 * One member's bids and RFQs, shown beneath the members list.
 *
 * OWNER/ADMIN only — the caller is responsible for not rendering this for a
 * plain MEMBER, and the endpoint enforces the same rule with a 403.
 */
export const MemberInquiriesPanel: React.FC<MemberInquiriesPanelProps> = ({
  orgId,
  member,
  isMobile,
  onClose,
}) => {
  const [offset, setOffset] = useState(0)
  const { inquiries, isLoading, error } = useOrgMemberInquiries(orgId, member.user_id, {
    limit: PAGE_LIMIT,
    offset,
  })

  const items = useMemo(() => mergeMemberInquiries(inquiries), [inquiries])
  const totalPages = mergedPageCount(inquiries, PAGE_LIMIT)
  const currentPage = Math.floor(offset / PAGE_LIMIT) + 1

  const label = memberName(member)
  const bidsTotal = inquiries?.bids?.total ?? 0
  const rfqsTotal = inquiries?.rfqs?.total ?? 0

  return (
    <div
      className={cn(ORG_CARD, isMobile ? ORG_CARD_PADDING_MOBILE : ORG_CARD_PADDING_DESKTOP)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={ORG_HEADING}>{label}</h2>
            <RoleBadge role={member.role} />
          </div>
          <p className={cn('mt-1', ORG_SUBHEADING)}>
            {isLoading
              ? 'Loading activity...'
              : `${bidsTotal} ${bidsTotal === 1 ? 'bid' : 'bids'} · ${rfqsTotal} ${
                  rfqsTotal === 1 ? 'RFQ' : 'RFQs'
                }`}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close member activity"
          className="shrink-0 p-1 text-[rgba(45,41,35,0.64)] transition-colors hover:text-[#2d2923] dark:text-[rgba(253,253,253,0.64)] dark:hover:text-[#fdfdfd]"
        >
          <X size={20} />
        </button>
      </div>

      {error && <OrgErrorBanner message={error} className="mt-4" />}

      <div className="mt-4">
        {isMobile ? (
          <MemberInquiriesMobile memberLabel={label} items={items} isLoading={isLoading} />
        ) : (
          <MemberInquiriesDesktop memberLabel={label} items={items} isLoading={isLoading} />
        )}
      </div>

      {totalPages > 1 && (
        <>
          <PaginationWrapper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setOffset((page - 1) * PAGE_LIMIT)}
            className="mt-4"
          />
          {/*
            Bids and RFQs paginate independently against one shared limit/offset,
            so a page can be short on one kind while the other still has rows.
          */}
          <p className={cn('mt-2 text-center', ORG_SUBHEADING)}>
            Bids and RFQs are paged separately.
          </p>
        </>
      )}
    </div>
  )
}

export default MemberInquiriesPanel
